import React, { useState, useEffect, useCallback } from 'react';
import { MsalProvider, useMsal, useIsAuthenticated, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { loginRequest } from './authConfig';
import { authenticateWithAzure, getVersion } from './api';
import Dashboard from './Dashboard';
import CalendarioEquipo from './CalendarioEquipo';
import Enchufeitor from './Enchufeitor';
import Guardias from './Guardias';
import Equipo from './Equipo';
import PartesSemanales from './PartesSemanales';
import Config from './Config';
import ClientesHoras from './ClientesHoras';
import Helpdesk from './Helpdesk';
import './App.css';

// Componente de reloj
function Clock() {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatDate = (date) => {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${dayName}, ${day} de ${month} de ${year}`;
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="clock-widget">
      <div className="clock-time">{formatTime(time)}</div>
      <div className="clock-date">{formatDate(time)}</div>
    </div>
  );
}

function CalendarApp() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [currentSection, setCurrentSection] = useState('dashboard');
  const [version, setVersion] = useState(null);
  const [helpdeskOpen, setHelpdeskOpen] = useState(false);

  // Manejar autenticación con el backend
  const authenticateBackend = useCallback(async () => {
    console.log('[AUTH] authenticateBackend called', {
      isAuthenticated,
      accountsLength: accounts.length,
      hasUser: !!user
    });

    if (isAuthenticated && accounts.length > 0 && !user) {
      console.log('[AUTH] Starting backend authentication process');
      setIsLoading(true);
      setAuthError(null);
      try {
        const account = accounts[0];
        console.log('[AUTH] Account:', account.username);

        // Obtener token de acceso
        console.log('[AUTH] Acquiring token silently...');
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: account
        });

        console.log('[AUTH] Token acquired successfully, authenticating with backend...');
        console.log('[AUTH] Access token:', response.accessToken.substring(0, 50) + '...');

        // Autenticar con el backend
        const authResponse = await authenticateWithAzure(response.accessToken);
        console.log('[AUTH] Backend authentication successful:', authResponse.data);

        localStorage.setItem('token', authResponse.data.token);
        setUser(authResponse.data.user);
        console.log('[AUTH] User authenticated:', authResponse.data.user);
      } catch (error) {
        console.error('[AUTH] Error during authentication:', {
          name: error.name,
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setAuthError('Error al autenticar con el servidor');

        // Si falla la autenticación silenciosa, intentar interactiva
        if (error.name === 'InteractionRequiredAuthError') {
          console.log('[AUTH] Interaction required, redirecting...');
          try {
            await instance.acquireTokenRedirect(loginRequest);
          } catch (redirectError) {
            console.error('[AUTH] Error in redirect:', redirectError);
          }
        }
      } finally {
        setIsLoading(false);
        console.log('[AUTH] authenticateBackend finished');
      }
    } else {
      console.log('[AUTH] Skipping authentication:', {
        reason: !isAuthenticated ? 'not authenticated' :
                accounts.length === 0 ? 'no accounts' :
                user ? 'user already set' : 'unknown'
      });
    }
  }, [isAuthenticated, accounts, instance, user]);

  useEffect(() => {
    authenticateBackend();

    // Verificar si hay token en localStorage (dev login)
    const token = localStorage.getItem('token');
    if (token && !user && !isAuthenticated) {
      // Obtener info del usuario desde el token
      fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(res => res.json())
      .then(data => {
        setUser(data);
        console.log('[DEV-LOGIN] User restored from token:', data);
      })
      .catch(err => {
        console.error('[DEV-LOGIN] Failed to restore session:', err);
        localStorage.removeItem('token');
      });
    }
  }, [authenticateBackend, user, isAuthenticated]);

  // Cargar versión
  useEffect(() => {
    getVersion()
      .then(res => setVersion(res.data.version))
      .catch(() => setVersion('unknown'));
  }, []);

  const handleLogin = async () => {
    console.log('[LOGIN] Login button clicked');
    console.log('[LOGIN] Current location:', window.location.href);
    console.log('[LOGIN] Login request config:', loginRequest);
    try {
      console.log('[LOGIN] Initiating loginRedirect...');
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('[LOGIN] Error during login:', error);
      setAuthError('Error al iniciar sesión');
    }
  };

  // DEV LOGIN - Bypass SSO temporalmente
  const handleDevLogin = async () => {
    setIsLoading(true);
    setAuthError(null);
    try {
      const email = 'lorenzo.morales@cloudappi.net'; // Email por defecto
      const response = await fetch('/api/auth/dev-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        throw new Error('Dev login failed');
      }

      const data = await response.json();
      localStorage.setItem('token', data.token);
      setUser(data.user);
      console.log('[DEV-LOGIN] User authenticated:', data.user);
    } catch (error) {
      console.error('[DEV-LOGIN] Error:', error);
      setAuthError('Error en dev login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    instance.logoutRedirect({
      postLogoutRedirectUri: '/'
    });
  };

  const renderSection = () => {
    switch (currentSection) {
      case 'dashboard':
        return <Dashboard />;
      case 'calendario':
        return <CalendarioEquipo />;
      case 'enchufeitor':
        return <Enchufeitor />;
      case 'guardias':
        return <Guardias />;
      case 'equipo':
        return <Equipo />;
      case 'partes':
        return <PartesSemanales />;
      case 'clientes-horas':
        return <ClientesHoras />;
      case 'config':
        return <Config />;
      default:
        return <Dashboard />;
    }
  };

  // Renderizar la app si hay usuario (Azure o Dev)
  if (user && !isLoading) {
    return (
      <div className="app">
        <header className="app-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <img
              src="/img/Logo-web-Cloudappi-white.png"
              alt="CloudAppi"
              style={{ height: '35px' }}
            />
            <h1>Calendario del Equipo</h1>
          </div>
          <div className="user-info">
            {user.photo_url && (
              <img
                src={user.photo_url}
                alt={user.name}
                className="user-photo"
              />
            )}
            <span>{user.name} {user.role === 'admin' && '(Admin)'}</span>
            {user.role === 'admin' && (
              <button
                onClick={() => setCurrentSection('config')}
                className="config-button"
                title="Configuración del sistema"
              >
                ⚙️
              </button>
            )}
            <Clock />
            <button onClick={handleLogout} className="logout-button">Cerrar sesión</button>
          </div>
        </header>

        <nav className="section-nav">
          <button
            className={`section-button ${currentSection === 'dashboard' ? 'active' : ''}`}
            onClick={() => setCurrentSection('dashboard')}
          >
            Dashboard
          </button>
          <button
            className={`section-button ${currentSection === 'calendario' ? 'active' : ''}`}
            onClick={() => setCurrentSection('calendario')}
          >
            Calendario Equipo
          </button>
          <button
            className={`section-button ${currentSection === 'enchufeitor' ? 'active' : ''}`}
            onClick={() => setCurrentSection('enchufeitor')}
          >
            Enchufeitor
          </button>
          <button
            className={`section-button ${currentSection === 'guardias' ? 'active' : ''}`}
            onClick={() => setCurrentSection('guardias')}
          >
            Guardias
          </button>
          <button
            className={`section-button ${currentSection === 'equipo' ? 'active' : ''}`}
            onClick={() => setCurrentSection('equipo')}
          >
            Equipo
          </button>
          <button
            className={`section-button ${currentSection === 'partes' ? 'active' : ''}`}
            onClick={() => setCurrentSection('partes')}
          >
            Partes Semanales
          </button>
          <button
            className={`section-button ${currentSection === 'clientes-horas' ? 'active' : ''}`}
            onClick={() => setCurrentSection('clientes-horas')}
          >
            Clientes/Horas
          </button>
        </nav>

        <div className="section-content">
          {renderSection()}
        </div>

        <footer className="app-footer">
          <span>Calendar © {new Date().getFullYear()} CloudAppi</span>
          {version && <span className="version">v{version}</span>}
        </footer>

        {/* Botón flotante de Helpdesk */}
        <button
          className="helpdesk-float-button"
          onClick={() => setHelpdeskOpen(true)}
          title="Abrir asistente Helpdesk"
        >
          <img src="/images/hellbot.png?v=2.13.2" alt="Helpdesk" />
        </button>

        {/* Modal de Helpdesk */}
        <Helpdesk
          isOpen={helpdeskOpen}
          onClose={() => setHelpdeskOpen(false)}
        />
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="loading-container">
        <p>Cargando...</p>
      </div>
    );
  }

  // Login screen
  return (
    <div className="login-container">
      <div className="login-box">
        <div className="logo-container">
          <img
            src="/img/Logo-web-Cloudappi-white.png"
            alt="CloudAppi Logo"
          />
        </div>
        <h1>Calendario del Equipo</h1>
        <p>Gestión de eventos, guardias y ausencias del equipo DevOps</p>
        {authError && (
          <div style={{ color: '#d32f2f', marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#ffebee', borderRadius: '8px', fontSize: '0.9rem' }}>
            {authError}
          </div>
        )}
        <button onClick={handleLogin} className="login-button">
          Iniciar sesión con Microsoft
        </button>
      </div>
    </div>
  );
}

function App({ msalInstance }) {
  return (
    <MsalProvider instance={msalInstance}>
      <CalendarApp />
    </MsalProvider>
  );
}

export default App;
